/**
 * Frame loading for scroll-driven image sequences, bounded in network and in memory.
 *
 * Network: nothing is fetched before start(). Then each frame is fetched once as compressed bytes
 * (5–60 KB), at most `maxConcurrent` at a time — frame 1 first, then the frames around the scroll
 * position, more of them ahead in the scroll direction than behind, and the faster the scroll the
 * further ahead. Once the first frame is on screen the rest fills in at once (never with
 * Save-Data): a visitor who scrolls straight away needs the frames now, not when the page is idle.
 * A set shipped as `bundles` (the small preview) comes in one request per file, in order, and is
 * sliced locally.
 *
 * Memory: compressed bytes are decoded off the main thread (createImageBitmap) for a window
 * around the scroll position only — several at a time, because one decode has a high latency
 * (≈160 ms for a 1920 px frame, ≈4 ms for a 320 px one) but they run in parallel. Decoded frames
 * are held within a byte budget; when it is full, the frames farthest from the scroll position
 * are released. Drawing never decodes: it shows the nearest decoded frame, so a fast jump never
 * leaves the canvas blank and never stalls a scroll.
 */

export type Drawable = ImageBitmap | HTMLImageElement;

export const enum FrameState {
  Idle = 0,
  Loading = 1,
  Loaded = 2,
  Failed = 3,
}

/** Part of a set in one file: `frames[k]` is the `offsets[k]` = [start, length] slice of it. */
export interface FrameBundle {
  url: string;
  frames: readonly number[];
  offsets: readonly (readonly [number, number])[];
}

export interface FrameLoaderOptions {
  frameCount: number;
  urlFor: (index: number) => string;
  /** Decoded size of one frame (width × height × 4 bytes), for the memory budget. */
  frameBytes: number;
  /** Budget for decoded frames. Default 160 MB (phones: pass less). Never below the decode window. */
  maxDecodedBytes?: number;
  maxConcurrent?: number;
  /** Frames fetched ahead of / behind the scroll position before anything else (at rest). */
  ahead?: number;
  behind?: number;
  /** Frames decoded ahead of / behind the scroll position (at rest). */
  decodeAhead?: number;
  decodeBehind?: number;
  /** Decodes in flight at once. */
  decodeParallel?: number;
  /** Background order: every Nth frame first, so any scroll position soon has a close frame (0: off). */
  coarseStep?: number;
  /** Fill in the whole set from the start, without waiting for startBackground(). */
  eager?: boolean;
  /** 'high': every fetch at high priority (the preview must come first); 'auto': frame 1 and the focus. */
  priority?: 'auto' | 'high' | 'low';
  /** The set as a few files, fetched one after the other at start() (frame 1 is still fetched on its own, for the first paint). */
  bundles?: FrameBundle[];
  /** Every bundle arrived (true), or one will not come (false — the frames are fetched one by one instead). */
  onBundle?: (ok: boolean) => void;
  /** Injectable for tests. */
  fetchFrame?: (url: string, highPriority: boolean, signal: AbortSignal) => Promise<Blob>;
  decodeFrame?: (blob: Blob) => Promise<Drawable>;
  releaseFrame?: (frame: Drawable) => void;
  whenIdle?: (fn: () => void) => void;
  retryDelayMs?: number;
  onFrameReady?: (index: number) => void;
}

const MAX_ATTEMPTS = 3;
/** How far ahead to fetch / decode per frame-per-second of scroll speed (seconds of lookahead). */
const FETCH_LOOKAHEAD_S = 0.8;
const DECODE_LOOKAHEAD_S = 0.35;
const MAX_DECODE_AHEAD = 48;

async function defaultFetch(url: string, highPriority: boolean, signal: AbortSignal): Promise<Blob> {
  const res = await fetch(url, { signal, priority: highPriority ? 'high' : 'low' } as RequestInit);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
}

async function defaultDecode(blob: Blob): Promise<Drawable> {
  if (typeof createImageBitmap === 'function') return createImageBitmap(blob);
  const img = new Image();
  img.src = URL.createObjectURL(blob);
  await img.decode();
  return img;
}

function defaultRelease(frame: Drawable): void {
  if ('close' in frame) frame.close();
  else URL.revokeObjectURL(frame.src);
}

/** After the current task: the first frame is painted before the rest starts downloading. */
function defaultIdle(fn: () => void): void {
  setTimeout(fn, 0);
}

const saveData = () => Boolean((navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData);

export class FrameLoader {
  readonly states: Uint8Array;
  private readonly blobs: (Blob | null)[];
  private readonly attempts: Uint8Array;
  private readonly decoded = new Map<number, Drawable>();
  private readonly decoding = new Set<number>();
  private readonly controller = new AbortController();
  private readonly opts: Required<Omit<FrameLoaderOptions, 'onFrameReady' | 'bundles' | 'onBundle'>> & Pick<FrameLoaderOptions, 'onFrameReady' | 'bundles' | 'onBundle'>;
  readonly maxDecoded: number;
  private focus = 0;
  private direction: 1 | -1 = 1;
  /** Scroll speed in frames of this set per second (0 at rest). */
  private speed = 0;
  private active = 0;
  private started = false;
  private background = false;
  private bundleAttempts = 0;
  private destroyed = false;

  constructor(options: FrameLoaderOptions) {
    this.opts = {
      maxDecodedBytes: 160 * 1024 * 1024,
      maxConcurrent: 6,
      ahead: 32,
      behind: 8,
      decodeAhead: 8,
      decodeBehind: 3,
      decodeParallel: 8,
      coarseStep: 8,
      eager: false,
      priority: 'auto',
      fetchFrame: defaultFetch,
      decodeFrame: defaultDecode,
      releaseFrame: defaultRelease,
      whenIdle: defaultIdle,
      retryDelayMs: 1500,
      ...options,
    };
    this.states = new Uint8Array(options.frameCount);
    this.attempts = new Uint8Array(options.frameCount);
    this.blobs = new Array(options.frameCount).fill(null);
    // Never fewer than the decode window at rest, whatever the budget.
    this.maxDecoded = Math.max(this.opts.decodeAhead + this.opts.decodeBehind + 2, Math.floor(this.opts.maxDecodedBytes / Math.max(1, options.frameBytes)));
    this.background = this.opts.eager && !saveData();
  }

  get loadedCount(): number {
    let n = 0;
    for (const s of this.states) if (s === FrameState.Loaded) n++;
    return n;
  }

  get decodedCount(): number {
    return this.decoded.size;
  }

  /** Whether this set comes as bundle files (until one has proven unavailable). */
  get bundled(): boolean {
    return Boolean(this.opts.bundles?.length);
  }

  /** Fetch window ahead of the focus: at least `ahead`, more when scrolling fast. */
  get aheadNow(): number {
    return Math.min(this.opts.frameCount, Math.max(this.opts.ahead, Math.round(this.speed * FETCH_LOOKAHEAD_S)));
  }

  /** Decode window ahead of the focus: at least `decodeAhead`, more when scrolling fast. */
  get decodeAheadNow(): number {
    return Math.min(MAX_DECODE_AHEAD, Math.max(this.opts.decodeAhead, Math.round(this.speed * DECODE_LOOKAHEAD_S)));
  }

  /** Begin fetching. Until then setFocus() only records where the scroll is. */
  start(): void {
    if (this.started) return;
    this.started = true;
    this.pump();
    if (this.bundled) this.loadBundles(0);
  }

  /** Move the priority window (on every change of the frame the scroll position asks for). */
  setFocus(index: number, direction?: 1 | -1, speed?: number): void {
    this.focus = Math.max(0, Math.min(this.opts.frameCount - 1, Math.round(index)));
    if (direction) this.direction = direction;
    if (speed !== undefined) this.speed = Math.max(0, speed);
    this.pump();
    this.pumpDecode();
  }

  /** Let the rest of the set fill in (call once the first frame is drawn). */
  startBackground(): void {
    if (this.background || this.destroyed || saveData()) return;
    this.opts.whenIdle(() => {
      if (this.destroyed) return;
      this.background = true;
      this.pump();
    });
  }

  /** Next frame to fetch, or -1 when nothing more should be fetched right now. */
  nextIndex(): number {
    const { frameCount, behind, coarseStep } = this.opts;
    const idle = (i: number) => i >= 0 && i < frameCount && this.states[i] === FrameState.Idle;
    if (idle(0)) return 0;
    if (this.bundled) return -1; // the bundles bring everything else
    const dir = this.direction;
    const ahead = this.aheadNow;
    for (let d = 0; d <= ahead; d++) {
      if (idle(this.focus + d * dir)) return this.focus + d * dir;
      if (d > 0 && d <= behind && idle(this.focus - d * dir)) return this.focus - d * dir;
    }
    if (!this.background) return -1;
    if (coarseStep > 0) for (let i = 0; i < frameCount; i += coarseStep) if (idle(i)) return i;
    // Then everything still to come in the scroll direction, then what lies behind.
    for (let i = this.focus + dir; i >= 0 && i < frameCount; i += dir) if (idle(i)) return i;
    for (let i = this.focus - dir; i >= 0 && i < frameCount; i -= dir) if (idle(i)) return i;
    return -1;
  }

  /** The decoded frame nearest to `index` — the one just passed in the scroll direction first. */
  nearestDecoded(index: number): number {
    const n = this.opts.frameCount;
    const i = Math.max(0, Math.min(n - 1, index));
    if (this.decoded.has(i)) return i;
    for (let d = 1; d < n; d++) {
      const back = i - d * this.direction;
      const fwd = i + d * this.direction;
      if (back >= 0 && back < n && this.decoded.has(back)) return back;
      if (fwd >= 0 && fwd < n && this.decoded.has(fwd)) return fwd;
    }
    return -1;
  }

  frame(index: number): Drawable | undefined {
    return this.decoded.get(index);
  }

  destroy(): void {
    this.destroyed = true;
    this.controller.abort();
    for (const frame of this.decoded.values()) this.opts.releaseFrame(frame);
    this.decoded.clear();
    this.blobs.fill(null);
  }

  private pump(): void {
    if (this.destroyed || !this.started) return;
    while (this.active < this.opts.maxConcurrent) {
      const index = this.nextIndex();
      if (index < 0) return;
      this.request(index);
    }
  }

  private isHigh(index: number): boolean {
    if (this.opts.priority === 'high') return true;
    if (this.opts.priority === 'low') return false;
    return index === 0 || index === this.focus;
  }

  private request(index: number): void {
    this.states[index] = FrameState.Loading;
    this.active++;
    this.opts
      .fetchFrame(this.opts.urlFor(index), this.isHigh(index), this.controller.signal)
      .then((blob) => {
        if (this.destroyed) return;
        this.blobs[index] = blob;
        this.states[index] = FrameState.Loaded;
        this.pumpDecode();
      })
      .catch(() => {
        if (this.destroyed) return;
        this.states[index] = FrameState.Failed;
        this.attempts[index]!++;
        // A flaky frame is tried again later; a missing one gives up — neighbours stand in for it.
        if (this.attempts[index]! < MAX_ATTEMPTS) {
          setTimeout(() => {
            if (this.destroyed || this.states[index] !== FrameState.Failed) return;
            this.states[index] = FrameState.Idle;
            this.pump();
          }, this.opts.retryDelayMs * this.attempts[index]!);
        }
      })
      .finally(() => {
        this.active--;
        this.pump();
      });
  }

  /** The bundle files one after the other, so the first (which covers the whole set) has the line to itself. */
  private loadBundles(part: number): void {
    const bundle = this.opts.bundles?.[part];
    if (!bundle) {
      this.opts.onBundle?.(true);
      return;
    }
    this.opts
      .fetchFrame(bundle.url, true, this.controller.signal)
      .then((whole) => {
        if (this.destroyed) return;
        bundle.frames.forEach((i, k) => {
          const slice = bundle.offsets[k];
          if (!slice || i < 0 || i >= this.opts.frameCount || this.states[i] === FrameState.Loaded) return;
          this.blobs[i] = whole.slice(slice[0], slice[0] + slice[1], 'image/webp');
          this.states[i] = FrameState.Loaded;
        });
        this.bundleAttempts = 0;
        this.pumpDecode();
        this.loadBundles(part + 1);
      })
      .catch(() => {
        if (this.destroyed) return;
        if (++this.bundleAttempts < MAX_ATTEMPTS) {
          setTimeout(() => !this.destroyed && this.loadBundles(part), this.opts.retryDelayMs * this.bundleAttempts);
          return;
        }
        // This file will not come: the frames still missing are fetched one by one instead.
        this.opts.bundles = undefined;
        this.pump();
        this.opts.onBundle?.(false);
      });
  }

  /** Decode the frames around the scroll position: the focus, then ahead, then just behind. */
  private pumpDecode(): void {
    if (this.destroyed) return;
    const { frameCount, decodeBehind, decodeParallel } = this.opts;
    const decodeAhead = this.decodeAheadNow;
    const wanted: number[] = [this.focus];
    for (let d = 1; d <= Math.max(decodeAhead, decodeBehind); d++) {
      if (d <= decodeAhead) wanted.push(this.focus + d * this.direction);
      if (d <= decodeBehind) wanted.push(this.focus - d * this.direction);
    }
    for (const i of wanted) {
      if (this.decoding.size >= decodeParallel) return;
      if (i < 0 || i >= frameCount || this.decoded.has(i) || this.decoding.has(i)) continue;
      const blob = this.blobs[i];
      if (blob) this.decode(i, blob);
    }
  }

  private decode(index: number, blob: Blob): void {
    this.decoding.add(index);
    this.opts
      .decodeFrame(blob)
      .then((frame) => {
        if (this.destroyed) return this.opts.releaseFrame(frame);
        this.decoded.set(index, frame);
        this.evict();
        this.opts.onFrameReady?.(index);
      })
      .catch(() => {
        // A frame that does not decode is skipped; its neighbours are drawn instead.
      })
      .finally(() => {
        this.decoding.delete(index);
        this.pumpDecode();
      });
  }

  /** Over budget: release the decoded frames farthest from the scroll position. */
  private evict(): void {
    const keep = Math.max(this.maxDecoded, this.decodeAheadNow + this.opts.decodeBehind + 2);
    while (this.decoded.size > keep) {
      let farthest = -1;
      let distance = -1;
      for (const i of this.decoded.keys()) {
        const d = Math.abs(i - this.focus);
        if (d > distance) {
          distance = d;
          farthest = i;
        }
      }
      const frame = this.decoded.get(farthest)!;
      this.decoded.delete(farthest);
      this.opts.releaseFrame(frame);
    }
  }
}

/** frame_0001.webp style URL builder (index is 0-based, file numbers are 1-based). */
export function frameUrl(baseUrl: string, prefix: string, extension: string, padLength: number, index: number): string {
  return `${baseUrl.replace(/\/$/, '')}/${prefix}${String(index + 1).padStart(padLength, '0')}.${extension}`;
}

/**
 * The frame to show for a position on the original 960-frame timeline: the kept frame nearest to
 * it (`sourceFrames` is ascending). Keeps the scroll timing of the full sequence exactly.
 */
export function frameForSource(sourceFrames: readonly number[], position: number): number {
  let lo = 0;
  let hi = sourceFrames.length - 1;
  if (position <= sourceFrames[0]!) return 0;
  if (position >= sourceFrames[hi]!) return hi;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (sourceFrames[mid]! <= position) lo = mid;
    else hi = mid;
  }
  return position - sourceFrames[lo]! <= sourceFrames[hi]! - position ? lo : hi;
}

/** Source rectangle for drawing an image into a canvas with object-fit semantics. */
export function fitRect(imgW: number, imgH: number, canvasW: number, canvasH: number, fit: 'cover' | 'contain') {
  const scale = fit === 'cover' ? Math.max(canvasW / imgW, canvasH / imgH) : Math.min(canvasW / imgW, canvasH / imgH);
  const w = imgW * scale;
  const h = imgH * scale;
  return { dx: (canvasW - w) / 2, dy: (canvasH - h) / 2, dw: w, dh: h };
}
